import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService.js';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', provider: 'all', status: '', page: 1 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [filters.page, filters.provider, filters.status]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.provider && filters.provider !== 'all') params.provider = filters.provider;
      if (filters.status) params.status = filters.status;

      const result = await adminService.getUsers(params);
      setUsers(result.users || []);
      setPagination(result.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (err) {
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(f => ({ ...f, page: 1 }));
    loadUsers();
  };

  const handleViewDetail = async (userId) => {
    try {
      setDetailLoading(true);
      setShowDetail(true);
      const result = await adminService.getUserDetail(userId);
      setSelectedUser(result);
    } catch (err) {
      toast.error('Erro ao carregar detalhes do usuário');
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentActive) => {
    try {
      await adminService.updateUserStatus(userId, !currentActive);
      toast.success(`Usuário ${!currentActive ? 'ativado' : 'desativado'} com sucesso`);
      loadUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser(prev => ({ ...prev, active: !currentActive }));
      }
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  const getProviderBadge = (provider) => {
    const badges = {
      local: { label: 'Email', class: 'badge-default' },
      google: { label: 'Google', class: 'badge-info' },
      apple: { label: 'Apple', class: 'badge-dark' },
    };
    return badges[provider] || { label: provider, class: 'badge-default' };
  };

  const formatDate = (d) => {
    const val = d || '';
    if (!val) return '-';
    const dt = dayjs(val);
    return dt.isValid() ? dt.format('DD/MM/YYYY') : '-';
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const digits = String(phone).replace(/\D/g, '');
    let d = digits;
    if (d.startsWith('55')) d = d.slice(2);
    if (d.length === 11) {
      return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
    }
    if (d.length === 10) {
      return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    }
    return phone;
  };

  return (
    <div className="admin-page">
      {/* Filters */}
      <div className="admin-filters">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Buscar por nome, email ou telefone..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              className="filter-control"
              style={{ minWidth: 320 }}
            />
          </div>
          <button type="submit" className="btn btn-primary filter-button">Buscar</button>
        </form>
        <div className="filter-group">
          <select value={filters.provider} onChange={(e) => setFilters(f => ({ ...f, provider: e.target.value, page: 1 }))} className="filter-control">
            <option value="all">Todos os Providers</option>
            <option value="local">Email</option>
            <option value="google">Google</option>
            <option value="apple">Apple</option>
          </select>
          <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))} className="filter-control">
            <option value="">Todos os Status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-card">
        <div className="table-header">
          <h3>Usuários ({pagination.total})</h3>
        </div>
        {loading ? (
          <div className="table-loading">
            {[1,2,3,4,5].map(i => <div key={i} className="shimmer shimmer-row"></div>)}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Data de criação</th>
                  <th>Verificado</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr><td colSpan="6" className="empty-table">Nenhum usuário encontrado</td></tr>
                ) : users.map(user => {
                  const badge = getProviderBadge(user.provider);
                  return (
                    <tr key={user.id}>
                      <td className="td-name">{user.name}</td>
                      <td>{user.email}</td>
                      <td>{formatPhone(user.phone)}</td>
                      <td><span className={`badge ${badge.class}`}>{badge.label}</span></td>
                      <td>
                        <span className={`badge ${user.active ? 'badge-success' : 'badge-danger'}`}>
                          {user.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td>{formatDate(user.createdAt || user.created_at || user.created)}</td>
                      <td>
                        <span className={`badge ${user.emailVerified ? 'badge-success' : 'badge-danger'}`}>
                          {user.emailVerified ? 'Verificado' : 'Não verificado'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="table-pagination">
            <button className="btn btn-sm" disabled={filters.page === 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Anterior</button>
            <span className="pagination-info">Página {pagination.page} de {pagination.totalPages}</span>
            <button className="btn btn-sm" disabled={filters.page >= pagination.totalPages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Próxima</button>
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalhes do Usuário</h3>
              <button className="modal-close" onClick={() => setShowDetail(false)}>&times;</button>
            </div>
            {detailLoading ? (
              <div className="modal-loading">
                <div className="loading-spinner"></div>
              </div>
            ) : selectedUser && (
              <div className="modal-body">
                <div className="detail-grid">
                  <div className="detail-section">
                    <h4>Informações Pessoais</h4>
                    <div className="detail-row"><span>Nome:</span><strong>{selectedUser.name}</strong></div>
                    <div className="detail-row"><span>Email:</span><strong>{selectedUser.email}</strong></div>
                    <div className="detail-row"><span>Telefone:</span><strong>{formatPhone(selectedUser.phone)}</strong></div>
                    <div className="detail-row"><span>Provider:</span><span className={`badge ${getProviderBadge(selectedUser.provider).class}`}>{getProviderBadge(selectedUser.provider).label}</span></div>
                    <div className="detail-row"><span>Status:</span><span className={`badge ${selectedUser.active ? 'badge-success' : 'badge-danger'}`}>{selectedUser.active ? 'Ativo' : 'Inativo'}</span></div>
                    <div className="detail-row"><span>Email verificado:</span><span className={`badge ${selectedUser.emailVerified ? 'badge-success' : 'badge-danger'}`}>{selectedUser.emailVerified ? 'Verificado' : 'Não verificado'}</span></div>
                  </div>
                  {selectedUser.Entities && selectedUser.Entities.length > 0 && (
                    <div className="detail-section">
                      <h4>Negócios ({selectedUser.Entities.length})</h4>
                      {selectedUser.Entities.map((entity, i) => (
                        <div key={i} className="detail-card-mini">
                          <strong>{entity.name}</strong>
                          <span>{entity.type}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedUser.Pets && selectedUser.Pets.length > 0 && (
                    <div className="detail-section">
                      <h4>Pets ({selectedUser.Pets.length})</h4>
                      {selectedUser.Pets.map((pet, i) => (
                        <div key={i} className="detail-card-mini">
                          <strong>{pet.name}</strong>
                          <span>{pet.species} - {pet.breed}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-actions">
                  <button className={`btn ${selectedUser.active ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggleStatus(selectedUser.id, selectedUser.active)}>
                    {selectedUser.active ? 'Desativar Usuário' : 'Ativar Usuário'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
